import React, { useCallback, useMemo, lazy, Suspense } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsPageView, useEffectOnce, usePathParam, Skeleton, Toast, Typography } from '@chilecompra/react-kit'

import { REACT_APP_MINVU_URL, REACT_APP_MOP_URL } from '../../config/settings/environment'
import { onGetMinvuRegistryThunk, onLoadInitialRegistriesThunk, onGetMopRegistryThunk } from './WorkRegister.actions'
import { Container, Link, Root } from './WorkRegister.styles'
import { DNI_PARAM } from './WorkRegister.constants'

const MinvuCard = lazy(() => import('./WorkRegister.minvu-card'))
const MopCard = lazy(() => import('./WorkRegister.mop-card'))

/**
 * The work register section container.
 */
export const WorkRegister = () => {
  const dispatch = useDispatch()
  const dni = usePathParam(DNI_PARAM)

  const {
    error,
    loading,
    minvu: {
      consultants: { data: minvuConsultants, searching: minvuConsultantsSearching },
      contractors: { data: minvuContractors, searching: minvuContractorsSearching }
    },
    mop: {
      consultants: {
        data: mopConsultants,
        searching: mopConsultantsSearching,
        registration: mopConsultantsRegistration
      },
      contractors: {
        data: mopContractors,
        searching: mopContractorsSearching,
        registration: mopContractorsRegistration
      }
    }
  } = useSelector(state => state.workRegister)

  const dontHaveData = useMemo(() => {
    return (
      !mopConsultants?.records?.content?.length &&
      !mopContractors?.records?.content?.length &&
      !minvuConsultants?.records?.content?.length &&
      !minvuContractors?.records?.content?.length
    )
  }, [
    mopConsultants?.records?.content?.length,
    mopContractors?.records?.content?.length,
    minvuConsultants?.records?.content?.length,
    minvuContractors?.records?.content?.length
  ])

  useAnalyticsPageView('/ficha/registro-obras')

  const handleFetchMinvuData = useCallback(
    ({ page, registerCode }) => {
      dispatch(onGetMinvuRegistryThunk({ dni, page, registerCode }))
    },
    [dispatch, dni]
  )

  const handleFetchMopData = useCallback(
    ({ page, registerCode }) => {
      dispatch(onGetMopRegistryThunk({ dni, page, registerCode }))
    },
    [dispatch, dni]
  )

  useEffectOnce(() => {
    dispatch(onLoadInitialRegistriesThunk({ dni }))
  })

  return (
    <Root>
      <Container direction="column">
        <Typography fontWeight="bold" variant="h4">
          Registro de Contratistas y Consultores de Obras
        </Typography>

        <Typography variant="subtitle">
          La información contenida en esta sección se obtiene directamente de la fuente oficial.
        </Typography>
      </Container>

      {loading && <Skeleton height={300} variant="rect" />}

      {!loading && error && (
        <Toast
          severity="error"
          textDirection="vertical"
          title="Esta información no está disponible por el momento."
          width="100%"
        >
          Intenta consultar más tarde, o accede a los sitios del{' '}
          <Link href={REACT_APP_MOP_URL} rel="noreferrer" target="_blank">
            Registro MOP
          </Link>
          , y{' '}
          <Link href={REACT_APP_MINVU_URL} rel="noreferrer" target="_blank">
            Registro MINVU
          </Link>
          .
        </Toast>
      )}

      {!loading && !error && dontHaveData && (
        <Toast
          severity="info"
          textDirection="vertical"
          title="Este proveedor no está informado en los servicios dispuestos por MOP y MINVU"
        >
          Para mayor información, accede a los sitios del{' '}
          <Link href={REACT_APP_MOP_URL} rel="noreferrer" target="_blank">
            Registro MOP
          </Link>
          , y{' '}
          <Link href={REACT_APP_MINVU_URL} rel="noreferrer" target="_blank">
            Registro MINVU
          </Link>
          .
        </Toast>
      )}

      {!loading && !error && !dontHaveData && (
        <>
          <Suspense fallback={<Skeleton height={300} variant="rect" />}>
            <MopCard
              data={mopConsultants?.records ?? {}}
              registration={mopConsultantsRegistration ?? null}
              title="Registro Contratistas MOP"
              onFetchData={handleFetchMopData}
              searching={mopConsultantsSearching}
            />
          </Suspense>

          <Suspense fallback={<Skeleton height={300} variant="rect" />}>
            <MopCard
              data={mopContractors?.records ?? {}}
              registration={mopContractorsRegistration ?? null}
              title="Registro Consultores MOP"
              onFetchData={handleFetchMopData}
              searching={mopContractorsSearching}
            />
          </Suspense>

          <Suspense fallback={<Skeleton height={300} variant="rect" />}>
            <MinvuCard
              data={minvuContractors}
              onFetchData={handleFetchMinvuData}
              searching={minvuContractorsSearching}
              title="Registro Contratistas MINVU"
            />
          </Suspense>

          <Suspense fallback={<Skeleton height={300} variant="rect" />}>
            <MinvuCard
              data={minvuConsultants}
              onFetchData={handleFetchMinvuData}
              searching={minvuConsultantsSearching}
              title="Registro Consultores MINVU"
            />
          </Suspense>
        </>
      )}
    </Root>
  )
}
